// Scroll-driven choreography for the isometric marks: the one-shot settle-in of the mint
// nest (start of the Latently room) and of the closing cube (above the closing heading).
// Mounted once by Landing.tsx: useEffect(() => initMarkChoreography(rootRef.current!), []).
//
// Everything here reads the DOM by data attribute only — it never imports a component file,
// so it stays decoupled from how MintNest.tsx / ClosingCube.tsx render their markup.
//
// This module used to also ride a dashed "traveler" down the right margin of "How we work"
// (a pure function of that section's scroll). The owner saw it mid-ride and asked what it
// was for; a stranger would too. It is gone — the closing cube simply settles in place.
//
// Why a hand-rolled scroll listener instead of animejs's onScroll() driver: onScroll() binds
// one animation's playback progress to one target/threshold pair; one shared rAF-batched
// listener checking both settle triggers together is simpler and cheaper. animate() and
// spring() are still used for the actual one-shot settle motion, and createScope() wraps that
// so scope.revert() sweeps up any settle animation still in flight on unmount.

import { animate, createScope, spring, utils } from 'animejs'
import { prefersReducedMotion } from './reducedMotion'

// "Scrolled 60% into view" is read as an IntersectionObserver-style visible-ratio check: at
// least 60% of the section's own height currently sits inside the viewport. The denominator
// is capped to the viewport height so this stays reachable even for a section taller than
// the viewport (otherwise the ratio could never reach 0.6 and the entrance would never play).
// Computed from getBoundingClientRect so it can share the same scroll/resize tick as
// everything else here instead of running a second, separate observer.
function isSixtyPercentIntoView(section: HTMLElement, viewportHeight: number): boolean {
  const rect = section.getBoundingClientRect()
  if (rect.height <= 0) return false
  const visibleTop = Math.max(rect.top, 0)
  const visibleBottom = Math.min(rect.bottom, viewportHeight)
  const visibleHeight = Math.max(visibleBottom - visibleTop, 0)
  const reachableHeight = Math.min(rect.height, viewportHeight)
  return visibleHeight / reachableHeight >= 0.6
}

export function initMarkChoreography(root: HTMLElement): () => void {
  if (prefersReducedMotion()) {
    // Zero listeners, zero animations — the DOM's own default state (settled marks) is
    // already the finished, correct state for this visitor.
    return () => {}
  }

  const scope = createScope({ root })

  const settleOnce = Array.from(
    root.querySelectorAll<HTMLElement>('[data-mark="mint-nest"], [data-mark="closing-cube"]')
  )

  // One-shot elements that have already played their settle animation, or were already past
  // their trigger point at init (in which case they never play at all — the guard the
  // contract asks for, so a mid-page refresh never replays an entrance that already happened).
  const settled = new WeakSet<HTMLElement>()

  // Elements not yet triggered start the page in their FINISHED DOM state (contract: nothing
  // may rest mid-animation with no JS), so their pre-trigger "from" state has to be applied
  // here, in JS, before anything can animate toward the identity transform.
  for (const el of settleOnce) {
    const section = el.closest<HTMLElement>('[data-mood]')
    if (!section) continue
    if (isSixtyPercentIntoView(section, window.innerHeight)) {
      settled.add(el)
      continue
    }
    utils.set(el, { translateY: 14, scale: 0.92 })
  }

  let frameQueued = false
  let frameHandle = 0

  function measureAndApply(): void {
    frameQueued = false
    const viewportHeight = window.innerHeight

    for (const el of settleOnce) {
      if (settled.has(el)) continue
      const section = el.closest<HTMLElement>('[data-mood]')
      if (!section) continue
      if (isSixtyPercentIntoView(section, viewportHeight)) {
        settled.add(el)
        // Wrapped in scope.execute() so this animation is registered as a revertible: only
        // animate() calls made synchronously inside an execute() callback get swept up by
        // scope.revert() below, and this one fires later, from a scroll tick, not from the
        // scope's own setup — without this wrapper an in-flight settle would keep ticking
        // against a since-unmounted node.
        scope.execute(() => {
          animate(el, {
            translateY: 0,
            scale: 1,
            ease: spring({ bounce: 0.15, duration: 500 }),
          })
        })
      }
    }
  }

  function onScrollOrResize(): void {
    if (frameQueued) return
    frameQueued = true
    frameHandle = requestAnimationFrame(measureAndApply)
  }

  window.addEventListener('scroll', onScrollOrResize, { passive: true })
  window.addEventListener('resize', onScrollOrResize)
  // Run once immediately so a page load that lands mid-scroll (a refresh, or a same-page
  // anchor jump) starts with correct mark states instead of waiting on a scroll.
  measureAndApply()

  return () => {
    window.removeEventListener('scroll', onScrollOrResize)
    window.removeEventListener('resize', onScrollOrResize)
    if (frameQueued) cancelAnimationFrame(frameHandle)
    scope.revert()
  }
}
